import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

import { FlexSpacerDirective } from '../../shared/directives/flex-spacer.directive';
import { GablePendingProposal } from '../../../../services/gable/gable.service';

export interface ApprovalDialogData {
    proposal: GablePendingProposal;
}

export type ApprovalDialogResult = 'approve' | 'reject' | null;

/**
 * Minimal V2 dialog for confirming or rejecting an AI-proposed write.
 *
 * Built from scratch (no Figma) per the plan. Renders the proposal summary
 * + a JSON preview of the payload + three buttons: Approve, Reject, Cancel.
 *
 * Returns `'approve' | 'reject' | null` via dialogRef.close so the parent
 * page can drive the actual HTTP commit.
 *
 * 👋 Heads-up on icons: like the rest of Gable, this
 * dialog uses the Phosphor webfont (`<i class="ph-bold ph-…">`) rather
 * than the V2 SVG sprite. Intentional — we're piloting Phosphor here to
 * evaluate it against the sprite pattern. Reviewer feedback that flags
 * "use the sprite instead" can be deferred until the comparison lands.
 */
@Component({
    selector: 'app-gable-approval-dialog',
    templateUrl: './approval-dialog.component.html',
    styleUrls: ['./approval-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        FlexSpacerDirective,
    ],
})
export class ApprovalDialogComponent {
    constructor(
        private _dialogRef: MatDialogRef<ApprovalDialogComponent, ApprovalDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: ApprovalDialogData,
    ) {}

    get payloadJson(): string {
        try {
            return JSON.stringify(this.data.proposal.payload, null, 2);
        } catch {
            return '(unable to serialize payload)';
        }
    }

    onApprove(): void {
        this._dialogRef.close('approve');
    }

    onReject(): void {
        this._dialogRef.close('reject');
    }

    onCancel(): void {
        this._dialogRef.close(null);
    }
}
